import React, { useState, useMemo, useEffect, useCallback } from 'react';
import ServerCard from './ServerCard';
import ServerList from './ServerList';
import SearchBar from './SearchBar';
import ServerStats from './ServerStats';
import Loading from './Loading';
import { serverAPI, ServerRecord } from '../utils/api';
import { ServerSummary } from '../types/server';
import { useRealtimeEvent } from '../hooks/useRealtimeEvent';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import StartupAdModal from './StartupAdModal';


const mapServerRecordToSummary = (server: ServerRecord): ServerSummary => ({
  id: server.id,
  name: server.name,
  slug: server.slug,
  description: server.description,
  shortDescription: server.shortDescription,
  tags: server.tags || [],
  status: server.status as any,
  createdAt: server.createdAt,
  updatedAt: server.updatedAt,
  ratingAverage: server.ratingAverage,
  ratingCount: server.ratingCount,
  favoriteCount: 0,
  currentPlayers: server.currentPlayers || 0,
  maxPlayers: server.maxPlayers || 0,
  websiteUrl: server.websiteUrl,
  downloadUrl: server.downloadUrl,
  featuredUntil: server.featuredUntil,
  bannerUrl: server.bannerUrl,
  onlineRating: server.onlineRating,
  managementRating: server.managementRating,
  gameplayRating: server.gameplayRating,
});

const Home: React.FC = () => {
  const { t } = useLanguage();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [servers, setServers] = useState<ServerSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [stats, setStats] = useState({ totalServers: 0, comingSoon: 0, newServers: 0 });
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);

  const loadServers = useCallback(async () => {
    try {
      setIsLoading(true);

      const promises: Promise<any>[] = [
        serverAPI.getAllServers(),
        serverAPI.getHomeStats()
      ];

      if (isAuthenticated) {
        promises.push(serverAPI.getFavorites());
      }

      const results = await Promise.all(promises);

      const serversRes = results[0];
      const statsRes = results[1];
      const favoritesRes = isAuthenticated ? results[2] : { data: [] };

      const mappedServers = serversRes.data.map(mapServerRecordToSummary);
      setServers(mappedServers);
      setStats({
        totalServers: statsRes.totalServers,
        comingSoon: statsRes.comingSoon,
        newServers: statsRes.newServers
      });

      if (favoritesRes && favoritesRes.data) {
        setFavoriteIds(favoritesRes.data.map((s: any) => s.id));
      } else {
        setFavoriteIds([]);
      }
    } catch (err) {
      console.error('Failed to load servers:', err);
      setError('Failed to load servers. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading) {
      loadServers();
    }
  }, [loadServers, authLoading]);

  const refreshFromRealtime = useCallback(() => {
    void loadServers();
  }, [loadServers]);

  useRealtimeEvent('server:created', refreshFromRealtime);
  useRealtimeEvent('server:updated', refreshFromRealtime);
  useRealtimeEvent('server:deleted', refreshFromRealtime);
  useRealtimeEvent('comment:created', refreshFromRealtime);
  useRealtimeEvent('comment:deleted', refreshFromRealtime);
  useRealtimeEvent('comment:voted', refreshFromRealtime);

  const filteredServers = useMemo<ServerSummary[]>(() => {
    if (!searchTerm) return servers;

    const term = searchTerm.toLowerCase();
    return servers.filter((server: ServerSummary) =>
      server.name.toLowerCase().includes(term) ||
      server.description.toLowerCase().includes(term) ||
      server.tags.some((tag: string) => tag.toLowerCase().includes(term))
    );
  }, [servers, searchTerm]);

  // Featured servers logic
  const featuredList = useMemo(() => {
    const now = new Date();
    const featured = servers.filter(s => s.featuredUntil && new Date(s.featuredUntil) > now);
    // Fallback to top rated or recent if no featured servers
    return featured.length > 0 ? featured : servers.slice(0, 5);
  }, [servers]);
  const featuredServer = featuredList[currentFeaturedIndex];

  // Auto-rotate featured server every 5 seconds
  useEffect(() => {
    if (!featuredList.length) {
      return;
    }

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentFeaturedIndex((prevIndex: number) => (prevIndex + 1) % featuredList.length);
        setIsTransitioning(false);
      }, 300); // Half of transition duration
    }, 5000);

    return () => clearInterval(interval);
  }, [featuredList.length]);

  // Manual navigation functions
  const goToNext = () => {
    if (!featuredList.length) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentFeaturedIndex((prevIndex: number) => (prevIndex + 1) % featuredList.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goToPrevious = () => {
    if (!featuredList.length) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentFeaturedIndex((prevIndex: number) => (prevIndex - 1 + featuredList.length) % featuredList.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goToSlide = (index: number) => {
    if (!featuredList.length) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentFeaturedIndex(index);
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <div className="container mx-auto py-2 px-4">
      <StartupAdModal />
      {isLoading && (
        <div className="my-16">
          <Loading message={t('home.loading')} />
        </div>
      )}

      {!isLoading && error && (
        <div className="bg-red-100 dark:bg-red-900/60 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-100 rounded-xl p-6 mb-8">
          {error}
        </div>
      )}

      {/* Hero Section - Welcome & Featured Server Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-6 md:mb-10 mt-0">
        {/* Left Side - Welcome Section */}
        <div className="flex flex-col justify-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 md:mb-6 pb-1 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent leading-tight">
            {t('home.welcome_title')}
          </h1>
          <p className="text-lg md:text-2xl text-gray-600 dark:text-gray-300 mb-4 md:mb-8 leading-relaxed">
            {t('home.welcome_subtitle')}
          </p>

          <div className="mt-2 md:mt-4">
            <ServerStats
              totalServers={stats.totalServers}
              comingSoon={stats.comingSoon}
              newServers={stats.newServers}
            />
          </div>
        </div>

        {/* Right Side - Featured Server Carousel */}
        <div className="bg-gradient-to-br from-indigo-200 via-indigo-300 to-slate-300 dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-2xl p-3 md:p-8 border-2 border-indigo-500 dark:border-gray-700 shadow-xl dark:shadow-2xl flex flex-col relative hover:border-indigo-600 transition-colors">
          <h2 className="text-xl md:text-3xl font-bold mb-4 md:mb-6 text-gray-900 dark:text-white text-center">{t('home.featured_title')}</h2>

          {/* Carousel Container */}
          <div className="flex-grow flex items-center">
            {/* Server Card with Fade Animation */}
            {featuredServer ? (
              <div
                className={`w-full transition-opacity duration-600 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
                style={{ transitionDuration: '600ms' }}
              >
                <ServerCard
                  id={featuredServer.id}
                  name={featuredServer.name}
                  description={featuredServer.shortDescription || featuredServer.description}
                  createdAt={featuredServer.createdAt}
                  rating={featuredServer.ratingAverage}
                  status={featuredServer.status}
                  banner={featuredServer.bannerUrl}
                  onlineRating={featuredServer.onlineRating}
                  managementRating={featuredServer.managementRating}
                  gameplayRating={featuredServer.gameplayRating}
                />
              </div>
            ) : (
              <div className="text-gray-400 text-center w-full">{t('home.no_featured')}</div>
            )}
          </div>

          {/* Carousel Indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {featuredList.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${index === currentFeaturedIndex
                  ? 'w-8 bg-blue-500'
                  : 'w-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }`}
                aria-label={`Go to server ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mb-8 md:mb-12 max-w-2xl mx-auto px-2">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder={t('home.search_placeholder')}
        />
      </div>

      <div className="mb-6 md:mb-10">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-8 text-gray-900 dark:text-white text-center">{t('home.all_pvp_servers')}</h2>
        <ServerList servers={filteredServers} isLoading={isLoading} favoriteIds={favoriteIds} />
      </div>
    </div>
  );
};

export default Home;